/**
 * Responsive image variants for locally-hosted images.
 *
 * Some image paths reach components as plain strings — most notably a
 * project's `cover_image`, which arrives from frontmatter. Vite cannot rewrite
 * a path held in a string, so those images live here as real modules instead,
 * and this maps a path to the variants `vite-imagetools` generated at build
 * time, keyed by filename.
 *
 * To add an image: drop the file anywhere under `theme/assets/` and reference
 * it by filename. There is no list to maintain. Filenames are the key, so keep
 * them unique across subdirectories.
 *
 * Consumers should use the `ResponsiveImage` primitive rather than calling
 * this directly.
 */

// Queries are raw strings, not objects: imagetools separates multiple widths
// with `;`, and the object form URL-encodes it, which silently collapses the
// srcset to a single entry at the source's original width.

/** `srcset` string per file, e.g. "/a.webp 560w, /b.webp 1152w, …" */
const srcsets = import.meta.glob<string>("./**/*.{png,jpg,jpeg}", {
  eager: true,
  import: "default",
  query: "?w=560;1152;2304&format=webp&as=srcset",
});

/** Single mid-size URL per file, used as the `src` fallback. */
const fallbacks = import.meta.glob<string>("./**/*.{png,jpg,jpeg}", {
  eager: true,
  import: "default",
  query: "?w=1152&format=webp",
});

function basename(path: string): string {
  return path.split("/").pop() ?? path;
}

function byFilename(record: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(record).map(([path, value]) => [basename(path), value]),
  );
}

const srcsetByName = byFilename(srcsets);
const fallbackByName = byFilename(fallbacks);

export interface ResolvedImage {
  /** Always set — the original path when no processed variant exists. */
  src: string;
  /** Undefined for images this module does not manage (e.g. remote URLs). */
  srcset?: string;
}

/**
 * Resolve an image path to its responsive variants.
 *
 * Unknown paths — remote URLs, or anything not under `theme/assets/` — pass
 * through unchanged with no `srcset`, so the image still renders rather than
 * breaking the page.
 */
export function resolveImage(path: string | undefined): ResolvedImage {
  if (!path) return { src: "" };
  const name = basename(path);
  return {
    src: fallbackByName[name] ?? path,
    srcset: srcsetByName[name],
  };
}
